import { useEffect, useRef } from 'react';

export default function CarePhilosophy() {
  const imgWrapRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    const el = imgWrapRef.current;
    const section = sectionRef.current;
    if (!el || !section) return;

    let ticking = false;
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

    // Two organic radii sets: initial (tighter/asymmetrical) → final (approved shape-3)
    const finalRadii = [63, 37, 47, 53, 58, 42, 55, 45];   // % pairs — approved resting shape
    const startRadii = [92, 8, 15, 85, 88, 12, 22, 78];    // dramatically tighter, more asymmetrical

    const update = () => {
      ticking = false;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const h = rect.height || 1;
      // Long scroll-linked range: begins as section enters viewport, ends after ~1 full viewport of scroll
      const travel = Math.max(vh * 0.95, h * 0.9);
      const raw = (vh - rect.top) / travel;
      const p = clamp(raw);
      // Custom easing: fast-ish initial opening, then a long slow settle over the last ~20%
      // Combines easeOutQuart for the body + a gentle tail
      const eased = p < 0.8
        ? (1 - Math.pow(1 - (p / 0.8), 4)) * 0.9
        : 0.9 + (1 - Math.pow(1 - ((p - 0.8) / 0.2), 2)) * 0.1;
      const e = eased;

      const r = finalRadii.map((f, i) => lerp(startRadii[i], f, e).toFixed(2));
      const scale = lerp(1.12, 1.0, e).toFixed(4);
      const opacity = lerp(0.65, 1, Math.min(1, e * 1.4)).toFixed(3);

      el.style.setProperty('--cp-radius',
        `${r[0]}% ${r[1]}% ${r[2]}% ${r[3]}% / ${r[4]}% ${r[5]}% ${r[6]}% ${r[7]}%`);
      el.style.setProperty('--cp-scale', scale);
      el.style.setProperty('--cp-opacity', opacity);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section id="approach" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden scroll-mt-[96px]" style={{ background: '#F2D16B' }}>
      <span className="deco-float-lg absolute pointer-events-none hidden md:block" style={{ width: 560, height: 560, top: -220, right: -180, background: '#F8E4A8', borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
      <span className="deco-float-sm-alt absolute pointer-events-none hidden md:block" style={{ width: 420, height: 420, bottom: -180, left: -140, background: '#EFC148', borderRadius: '52% 48% 39% 61% / 58% 44% 56% 42%', opacity: 0.55 }} aria-hidden />

      <div className="relative max-w-container mx-auto px-5 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-cocoa">Our Approach</div>
            <h2 className="mt-4 font-display font-bold text-[44px] md:text-[62px] leading-[0.98] text-cocoa">
              Less stress.<br/>
              More understanding.<br/>
              Better care.
            </h2>
            <p className="mt-7 text-[17px] md:text-[18px] text-ink/80 leading-relaxed max-w-[440px]">
              We take the time to read the room — your pet's body language, your questions, the pace that feels right — so the visit works for everyone.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div
              ref={imgWrapRef}
              className="cp-organic relative w-full aspect-[16/11] overflow-hidden shape-3 bg-white/40"
              style={{
                borderRadius: 'var(--cp-radius, 63% 37% 47% 53% / 58% 42% 55% 45%)',
                willChange: 'border-radius, transform, opacity',
                transition: 'border-radius 120ms linear',
              }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Bright%20wide%20editorial%20photograph%20of%20a%20calm%20veterinarian%20softly%20touching%20a%20black%20cats%20head%20while%20the%20cat%20owner%20watches%20with%20a%20warm%20reassuring%20smile%2C%20inside%20a%20bright%20modern%20warm%20veterinary%20exam%20room%20with%20large%20windows%20and%20abundant%20soft%20natural%20daylight%2C%20warm%20ivory%20walls%2C%20natural%20skin%20tones%2C%20quiet%20intimate%20trust%20moment%2C%20real%20clinical%20environment%2C%20natural%20bright%20photography&width=1080&height=744&seq=philosophy-yellow-3&orientation=landscape&nocache=true"
                alt="Care philosophy"
                className="w-full h-full object-cover object-top"
                style={{
                  transform: 'scale(var(--cp-scale, 1))',
                  opacity: 'var(--cp-opacity, 1)',
                  transition: 'transform 120ms linear, opacity 120ms linear',
                  willChange: 'transform, opacity',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}